import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import FolderOpenOutlinedIcon from "@mui/icons-material/FolderOpenOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import FinancialCoreApi from "../../../../../infrastructure/api/financialCore";
import BreadcrumbsComponent from "../../../../components/shared/breadcrumbs";
import EnhancedTable from "../../../../components/shared/table";
import { isManagementUser } from "../../../../utils/authRoleHelpers";
import LoanDisbursementExcelImportDialog from "../lending-queue/LoanDisbursementExcelImportDialog";
import LoanDisbursementSingleDialog from "../lending-queue/LoanDisbursementSingleDialog";

const SOURCE = "SolarentOperator";
const PAGE_TITLE = "واریز وام لندتک‌ها";
const WORKSPACE_BASE = "/SLN/SolarentOperator/ProjectWorkspace";

const pick = (obj, camel, pascal) => obj?.[camel] ?? obj?.[pascal] ?? null;

const formatMoney = (value) => {
  if (value == null || value === "") return "—";
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  return n.toLocaleString("fa-IR");
};

const formatDate = (value) => {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString("fa-IR", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
};

const PARTNER_FILTERS = [
  { value: "", label: "همه لندتک‌ها" },
  { value: "IRIA_PAY", label: "ایریا" },
  { value: "BARQ_PAY", label: "برق‌پی" },
];

const normalizeOverview = (raw) => {
  if (!raw) {
    return { totalCount: 0, totalAmount: 0, byPartner: [], items: [] };
  }
  const byPartner = pick(raw, "byPartner", "ByPartner") || [];
  const items = pick(raw, "items", "Items") || [];
  return {
    totalCount: pick(raw, "totalCount", "TotalCount") ?? 0,
    totalAmount: pick(raw, "totalAmount", "TotalAmount") ?? 0,
    byPartner: (Array.isArray(byPartner) ? byPartner : []).map((p) => ({
      lendingPartnerId: pick(p, "lendingPartnerId", "LendingPartnerId"),
      lendingPartnerCode:
        pick(p, "lendingPartnerCode", "LendingPartnerCode") || "",
      lendingPartnerTitle:
        pick(p, "lendingPartnerTitle", "LendingPartnerTitle") || "نامشخص",
      count: pick(p, "count", "Count") ?? 0,
      totalAmount: pick(p, "totalAmount", "TotalAmount") ?? 0,
    })),
    items: (Array.isArray(items) ? items : []).map((r) => ({
      id: pick(r, "id", "Id"),
      requestInfoId: pick(r, "requestInfoId", "RequestInfoId"),
      requestReferenceId: pick(r, "requestReferenceId", "RequestReferenceId"),
      applicantName: pick(r, "applicantName", "ApplicantName") || "—",
      applicantMobile: pick(r, "applicantMobile", "ApplicantMobile") || "—",
      lendingPartnerCode:
        pick(r, "lendingPartnerCode", "LendingPartnerCode") || "",
      lendingPartnerTitle:
        pick(r, "lendingPartnerTitle", "LendingPartnerTitle") || "نامشخص",
      amount: pick(r, "amount", "Amount") ?? 0,
      bankDocumentNumber:
        pick(r, "bankDocumentNumber", "BankDocumentNumber") || "—",
      disbursedAt: pick(r, "disbursedAt", "DisbursedAt"),
      sourceTitle: pick(r, "sourceTitle", "SourceTitle") || "—",
      description: pick(r, "description", "Description") || "",
      createOn: pick(r, "createOn", "CreateOn"),
    })),
  };
};

const LoanDisbursementsPage = () => {
  const navigate = useNavigate();
  const canEditAmount = isManagementUser();
  const [loading, setLoading] = useState(false);
  const [partnerCode, setPartnerCode] = useState("");
  const [search, setSearch] = useState("");
  const [searchDraft, setSearchDraft] = useState("");
  const [overview, setOverview] = useState(normalizeOverview(null));
  const [excelOpen, setExcelOpen] = useState(false);
  const [singleOpen, setSingleOpen] = useState(false);
  const [editDialog, setEditDialog] = useState({
    open: false,
    row: null,
    amount: "",
    reason: "",
    saving: false,
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await FinancialCoreApi.getLoanDisbursementsOverview(SOURCE, {
        partnerCode,
        search,
        take: 500,
      });
      if (res?.isSuccess === false) {
        toast.error(res?.message || res?.Message || "خطا در دریافت واریزها");
        setOverview(normalizeOverview(null));
        return;
      }
      setOverview(normalizeOverview(res?.data ?? res?.Data ?? res));
    } catch (e) {
      toast.error(e?.message || "خطا در دریافت واریزها");
      setOverview(normalizeOverview(null));
    } finally {
      setLoading(false);
    }
  }, [partnerCode, search]);

  useEffect(() => {
    load();
  }, [load]);

  const tableRows = useMemo(
    () =>
      overview.items.map((row, index) => ({
        ...row,
        row_index: index + 1,
        amountDisplay: formatMoney(row.amount),
        disbursedAtDisplay: formatDate(row.disbursedAt || row.createOn),
        requestDisplay: row.requestReferenceId || row.requestInfoId || "—",
      })),
    [overview.items]
  );

  const headCells = useMemo(
    () => [
      { id: "requestDisplay", label: "شماره درخواست" },
      { id: "applicantName", label: "متقاضی" },
      { id: "applicantMobile", label: "موبایل" },
      { id: "lendingPartnerTitle", label: "لندتک" },
      { id: "amountDisplay", label: "مبلغ واریزی" },
      { id: "bankDocumentNumber", label: "شماره سند" },
      { id: "sourceTitle", label: "منبع ثبت" },
      { id: "disbursedAtDisplay", label: "تاریخ واریز" },
    ],
    []
  );

  const handleSaveEdit = async () => {
    const { row, amount, reason } = editDialog;
    const amountNum = Number(String(amount).replace(/,/g, ""));
    if (!row?.id || !Number.isFinite(amountNum) || amountNum <= 0) {
      toast.error("مبلغ معتبر وارد کنید");
      return;
    }
    if (!String(reason || "").trim()) {
      toast.error("دلیل ویرایش الزامی است");
      return;
    }
    setEditDialog((s) => ({ ...s, saving: true }));
    try {
      const res = await FinancialCoreApi.updateRequestLoanDisbursement(SOURCE, {
        id: row.id,
        amount: amountNum,
        reason: String(reason).trim(),
      });
      if (res?.isSuccess === false) {
        toast.error(res?.message || "ویرایش مبلغ ناموفق بود");
        return;
      }
      toast.success(res?.message || "مبلغ واریز به‌روز شد");
      setEditDialog({ open: false, row: null, amount: "", reason: "", saving: false });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || "ویرایش مبلغ ناموفق بود");
    } finally {
      setEditDialog((s) => ({ ...s, saving: false }));
    }
  };

  const tableActions = useMemo(
    () => [
      {
        icon: <FolderOpenOutlinedIcon fontSize="small" />,
        color: "primary",
        tooltip: "باز کردن پرونده",
        hidden: (row) => !row.requestInfoId,
        onClick: (row) => navigate(`${WORKSPACE_BASE}/${row.requestInfoId}`),
      },
      {
        icon: <EditOutlinedIcon fontSize="small" />,
        color: "warning",
        tooltip: "ویرایش مبلغ (فقط مدیریت)",
        hidden: () => !canEditAmount,
        onClick: (row) =>
          setEditDialog({
            open: true,
            row,
            amount: String(row.amount ?? ""),
            reason: "",
            saving: false,
          }),
      },
    ],
    [canEditAmount, navigate]
  );

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <BreadcrumbsComponent />
      </Grid>

      <Grid item xs={12}>
        <Card>
          <CardHeader
            title={PAGE_TITLE}
            subheader="ثبت و بررسی مبلغ دقیق واریز وام به ازای هر پرونده — همه لندتک‌ها"
            action={
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={load}
                  disabled={loading}
                >
                  بروزرسانی
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  color="success"
                  startIcon={<UploadFileIcon />}
                  onClick={() => setSingleOpen(true)}
                  disabled={loading}
                >
                  ثبت واریز تکی
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  color="success"
                  startIcon={<UploadFileIcon />}
                  onClick={() => setExcelOpen(true)}
                  disabled={loading}
                >
                  اکسل واریز تجمیعی
                </Button>
              </Stack>
            }
          />
          <CardContent>
            <Stack spacing={2}>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 1.5,
                  alignItems: "stretch",
                }}
              >
                <Card
                  variant="outlined"
                  sx={{ minWidth: 220, bgcolor: "action.hover" }}
                >
                  <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
                    <Typography variant="caption" color="text.secondary">
                      مجموع کل واریزی‌ها
                    </Typography>
                    <Typography variant="h6" fontWeight={700}>
                      {formatMoney(overview.totalAmount)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {Number(overview.totalCount || 0).toLocaleString("fa-IR")}{" "}
                      ردیف
                    </Typography>
                  </CardContent>
                </Card>

                {overview.byPartner.map((p) => (
                  <Card
                    key={
                      p.lendingPartnerCode ||
                      p.lendingPartnerTitle ||
                      p.lendingPartnerId ||
                      "unknown"
                    }
                    variant="outlined"
                    sx={{ minWidth: 200 }}
                  >
                    <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="subtitle2" fontWeight={600}>
                          {p.lendingPartnerTitle}
                        </Typography>
                        {p.lendingPartnerCode ? (
                          <Chip size="small" label={p.lendingPartnerCode} />
                        ) : null}
                      </Stack>
                      <Typography variant="h6">
                        {formatMoney(p.totalAmount)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {Number(p.count || 0).toLocaleString("fa-IR")} ردیف
                      </Typography>
                    </CardContent>
                  </Card>
                ))}
              </Box>

              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1.5}
                alignItems={{ sm: "center" }}
              >
                <FormControl size="small" sx={{ minWidth: 180 }}>
                  <InputLabel id="loan-disb-partner-filter">لندتک</InputLabel>
                  <Select
                    labelId="loan-disb-partner-filter"
                    label="لندتک"
                    value={partnerCode}
                    onChange={(e) => setPartnerCode(e.target.value)}
                  >
                    {PARTNER_FILTERS.map((o) => (
                      <MenuItem key={o.value || "all"} value={o.value}>
                        {o.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  size="small"
                  label="جستجو (شماره سند / درخواست)"
                  value={searchDraft}
                  onChange={(e) => setSearchDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") setSearch(searchDraft.trim());
                  }}
                  sx={{ minWidth: 260 }}
                />
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setSearch(searchDraft.trim())}
                  disabled={loading}
                >
                  جستجو
                </Button>
              </Stack>

              <EnhancedTable
                rowKey="id"
                rows={tableRows}
                headCells={headCells}
                actions={tableActions}
                actionsMode="inline"
                loading={loading}
                pageSize={25}
              />
            </Stack>
          </CardContent>
        </Card>
      </Grid>

      <LoanDisbursementExcelImportDialog
        open={excelOpen}
        source={SOURCE}
        defaultPartnerCode={partnerCode || ""}
        onClose={() => setExcelOpen(false)}
        onCommitted={load}
      />
      <LoanDisbursementSingleDialog
        open={singleOpen}
        source={SOURCE}
        onClose={() => setSingleOpen(false)}
        onCommitted={load}
      />

      <Dialog
        open={editDialog.open}
        onClose={() =>
          !editDialog.saving &&
          setEditDialog({ open: false, row: null, amount: "", reason: "", saving: false })
        }
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>ویرایش مبلغ واریز</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
            درخواست {editDialog.row?.requestReferenceId || editDialog.row?.requestInfoId}
            {editDialog.row?.applicantName ? ` — ${editDialog.row.applicantName}` : ""}
          </Typography>
          <TextField
            fullWidth
            size="small"
            label="مبلغ واریزی (ریال)"
            value={editDialog.amount}
            onChange={(e) => setEditDialog((s) => ({ ...s, amount: e.target.value }))}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            size="small"
            label="دلیل ویرایش"
            value={editDialog.reason}
            onChange={(e) => setEditDialog((s) => ({ ...s, reason: e.target.value }))}
            multiline
            minRows={2}
            required
          />
        </DialogContent>
        <DialogActions>
          <Button
            disabled={editDialog.saving}
            onClick={() =>
              setEditDialog({ open: false, row: null, amount: "", reason: "", saving: false })
            }
          >
            انصراف
          </Button>
          <Button
            variant="contained"
            color="warning"
            disabled={editDialog.saving}
            onClick={handleSaveEdit}
          >
            ثبت ویرایش
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

export default LoanDisbursementsPage;
